/**
 * Naming_Flow gate predicate.
 *
 * The Naming_Flow is the first-launch interaction in which the AI_Companion
 * proposes its own name and the End_User accepts, requests another, or
 * provides a custom name (see Requirement 16). This module owns the single
 * boolean predicate the rest of the application uses to decide whether the
 * flow is currently active.
 *
 * Per the design's Naming_Flow gate (Property 31), the flow is active iff
 * no AI_Companion_Name has been persisted yet. We additionally treat a
 * whitespace-only persisted value as "no name persisted" so accidental
 * empty/whitespace writes still re-arm the flow on next boot.
 *
 * This module is PURE: it performs no I/O, has no side effects, depends
 * on no globals, and is safe to call from any layer (boot sequence, React
 * render, or property tests).
 *
 * Validates: Requirements 16.1, 16.10. Validates Property 31.
 */

/**
 * Returns true iff the Naming_Flow should be active.
 *
 * The flow is active iff no AI_Companion_Name has been persisted yet
 * (Requirements 16.1, 16.10).
 *
 * A whitespace-only persisted name is treated the same as `null` so that
 * accidental empty writes do not silently suppress the introduction.
 *
 * @param persistedName The AI_Companion_Name read from the
 *   Conversation_Store, or `null` when no value has been written yet.
 * @returns `true` when the Chat_Client should run the Naming_Flow on this
 *   boot, `false` when an existing name should be injected into the
 *   System_Prompt and shown in UI labels.
 *
 * Validates: Requirements 16.1, 16.10. Validates Property 31.
 */
export function isNamingFlowActive(persistedName: string | null): boolean {
  if (persistedName === null) return true;
  if (persistedName.trim() === '') return true;
  return false;
}
